import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'rst_admin_token';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [ready, setReady] = useState(false);

  // Attach token to every axios request + auto-redirect to login on 401
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t && config.url && config.url.startsWith(API)) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    const resId = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const isAuthApi = err?.config?.url && err.config.url.startsWith(API);
        const isBootstrap = err?.config?.url?.endsWith('/auth/me');
        if (err?.response?.status === 401 && isAuthApi && !isBootstrap) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
          const here = window.location.pathname + window.location.search;
          if (!here.startsWith('/admin/login')) {
            window.location.assign(`/admin/login?next=${encodeURIComponent(here)}&expired=1`);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  const hydrate = useCallback(async (t) => {
    const { data } = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    setUser(data);
    return data;
  }, []);

  // Try to load current user on mount
  useEffect(() => {
    const bootstrap = async () => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) {
        setReady(true);
        return;
      }
      try {
        await hydrate(t);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } finally {
        setReady(true);
      }
    };
    bootstrap();
  }, [hydrate]);

  const finishLogin = async (accessToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    setToken(accessToken);
    const me = await hydrate(accessToken);
    return me;
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    return finishLogin(data.access_token);
  };

  // ---- Email OTP -----------------------------------------------------------
  const requestOtp = async (email, purpose = 'login') => {
    const { data } = await axios.post(`${API}/auth/otp/request`, { email, purpose });
    return data;
  };

  const verifyLoginOtp = async (email, otp) => {
    const { data } = await axios.post(`${API}/auth/otp/verify-login`, { email, otp });
    return finishLogin(data.access_token);
  };

  const resetPassword = async (email, otp, newPassword) => {
    const { data } = await axios.post(`${API}/auth/password/reset`, {
      email, otp, new_password: newPassword,
    });
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {
      // Non-fatal: local session is cleared regardless of the server response.
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  // ---- Permission helpers --------------------------------------------------
  const can = useCallback(
    (slug, action = 'can_view') => {
      if (!user) return false;
      if (user.is_super) return true;
      const p = user.permissions?.[slug];
      return !!(p && p[action]);
    },
    [user]
  );

  const isSuper = !!user?.is_super;
  const isAdmin = isSuper || (user?.min_rank != null && user.min_rank <= 2);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        ready,
        login,
        logout,
        requestOtp,
        verifyLoginOtp,
        resetPassword,
        can,
        isSuper,
        isAdmin,
        menu: user?.menu || [],
        permissions: user?.permissions || {},
        refreshMe: () => token && hydrate(token),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
